import { usePresence, usePresenceData } from "motion/react";
import { PropsWithChildren, useEffect, useRef } from "react";
import {
  curtain,
  curtainShrinkAnimation,
  curtainGrowAnimation,
} from "./styles.css";

const getTransformOrigin = (direction: "up" | "down", closing?: boolean) => {
  if (closing) {
    return direction === "up" ? "bottom" : "top";
  }

  return direction === "up" ? "top" : "bottom";
};

export const AnimatedCurtain = ({ children }: PropsWithChildren<{}>) => {
  const scope = useRef<HTMLDivElement>(null);
  const [isPresent, safeToRemove] = usePresence();
  const { isEnabled, onClose, isOpenAnimationDisabled, direction } =
    usePresenceData();

  const onMountState =
    isEnabled && !isOpenAnimationDisabled ? "closed" : "opened";

  // Opening routine
  useEffect(() => {
    const element = scope.current;
    if (!element) {
      return;
    }

    if (onMountState === "closed") {
      element.style.setProperty(
        "transform-origin",
        getTransformOrigin(direction),
      );
      element.style.setProperty("animation-name", curtainShrinkAnimation);
    }
  }, []);

  // Closing routine
  useEffect(() => {
    const element = scope.current;

    if (!element) {
      return;
    }

    if (isPresent) {
      return;
    }

    if (!isEnabled) {
      return safeToRemove();
    }

    element.addEventListener(
      "animationend",
      ({ animationName }) => {
        if (animationName === curtainGrowAnimation) {
          onClose?.();
          safeToRemove();
        }
      },
      { once: true },
    );
    element.style.setProperty(
      "transform-origin",
      getTransformOrigin(direction, true),
    );
    element.style.setProperty("animation-name", curtainGrowAnimation);
  }, [isPresent, isEnabled, direction]);

  return (
    <>
      <div
        id="__transition-curtain"
        ref={scope}
        // set curtain initial visual state
        className={curtain[onMountState]}
      />
      {children}
    </>
  );
};
